<template>
  <div style="width: 100%">
    <!-- Comment Input -->
    <div class="d-flex flex-row" style="width: 100%" v-if="_id">
      <editable-avatar
        :image="profile.thumbnail || profile.image"
        :editable="false"
        :size="$vuetify.breakpoint.xs ? 30 : 40"
        class="mr-3"
      />
      <div style="width: 100%">
        <div class="d-flex flex-column comment-section">
          <div class="d-flex flex-row">
            <v-textarea
              v-model="text"
              placeholder="Add a comment..."
              rows="1"
              auto-grow
              dense
              flat
              solo
              hide-details
              style="width: 100%; border-radius: 20px"
              ref="comment"
            />

            <!-- Emoji Picker -->
            <v-menu
              bottom
              offset-y
              origin="bottom bottom"
              :close-on-content-click="false"
              v-model="emojiKeyboard"
            >
              <template v-slot:activator="{ on: menu, attrs }">
                <v-btn icon v-bind="attrs" v-on="menu">
                  <v-icon>mdi-emoticon-happy-outline</v-icon>
                </v-btn>
              </template>
              <v-card>
                <VEmojiPicker @select="selectEmoji" />
              </v-card>
            </v-menu>

            <!-- Image Upload -->
            <v-btn icon @click="onSelectImage">
              <v-icon>mdi-image-area</v-icon>
            </v-btn>
          </div>

          <!-- Preview Selected Image -->
          <div
            v-if="selectedFile"
            style="width: 100%; background-color:#f9fafb; border-radius: 0 0 20px 20px"
          >
            <v-divider />
            <div style="position:relative; width: 220px; border-radius: 20px">
              <v-img
                :src="selectedImage"
                alt="image attachment"
                max-width="200px"
                width="200px"
                class="ma-3 rounded-lg"
                contain
              />
              <v-btn
                @click="onRemoveImage"
                class="remove_button"
                fab
                x-small
                color="primary"
              >
                <v-icon size="16">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Post Button -->
        <v-btn
          color="primary"
          rounded
          class="mt-3"
          v-if="text || selectedFile"
          @click="onComment"
          :loading="loading"
        >
          Post
        </v-btn>
      </div>

      <!-- Hidden File Input -->
      <input
        ref="uploader"
        type="file"
        accept="image/*"
        class="d-none"
        @change="onFileChanged"
      />

      <!-- Snackbar for Errors -->
      <v-snackbar v-model="snackbar" top color="error">
        {{ errorMessage }}
      </v-snackbar>
    </div>

    <!-- Comment List -->
    <event-comment-item
      v-for="comment in comments"
      :key="comment._id"
      :comment="comment"
      :onUpdatedComment="onUpdatedComment"
      :onDeletedComment="onDeletedComment"
      class="mt-5"
    />

    <!-- Load More Button -->
    <v-btn
      text
      class="mt-3"
      small
      style="color: grey"
      :loading="loading"
      @click="loadMoreCommentsAction"
      v-if="total > comments.length"
    >
      Load more comments
    </v-btn>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import EventCommentItem from "./EventCommentItem.vue";

export default {
  components: { EditableAvatar, EventCommentItem },
  props: {
    _id: String,
    onPosted: Function,
    total: { type: Number, default: 0 },
    commentId: String
  },
  data() {
    return {
      text: "",
      selectedFile: null,
      selectedImage: "",
      emojiKeyboard: false,
      loading: false,
      errorMessage: null,
      snackbar: false,
      comments: []
    };
  },
  computed: {
    ...mapState("auth", { profile: "profile", profileType: "type" }),
    safeText() {
      return this.text.replace(/[<>]/g, ""); // Prevent XSS
    }
  },
  methods: {
    ...mapActions("event", [
      "setEventCommentLoaded",
      "comment",
      "getComments",
      "loadMoreComments"
    ]),

    selectEmoji(emoji) {
      this.insertText(emoji.data);
      this.makeFocus();
    },

    insertText(text) {
      const el = this.$refs.comment?.$refs.input;
      if (!el) return;
      const cursorPos = el.selectionEnd;
      this.text = this.text.slice(0, cursorPos) + text + this.text.slice(cursorPos);
      this.$nextTick(() => el.setSelectionRange(cursorPos + text.length, cursorPos + text.length));
    },

    makeFocus() {
      this.$refs.comment?.$refs.input?.focus();
    },

    onSelectImage() {
      this.$refs.uploader.value = null;
      this.$refs.uploader.click();
    },

    onFileChanged(e) {
      if (!e.target.files.length) return;
      this.selectedFile = e.target.files[0];
      this.selectedImage = URL.createObjectURL(this.selectedFile);
    },

    onRemoveImage() {
      this.selectedFile = null;
      this.selectedImage = "";
    },

    onComment() {
      if (!this.text && !this.selectedFile) return;

      const formData = new FormData();
      if (this.selectedFile) formData.append("file", this.selectedFile);
      if (this.text) formData.append("text", this.safeText);
      formData.append("_id", this._id);

      this.loading = true;
      this.comment(formData)
        .then(data => {
          this.loading = false;
          this.selectedFile = null;
          this.selectedImage = "";
          this.text = "";

          // Set poster info safely
          data.poster = this.profile._id;
          data.poster_type = this.profileType;
          data.poster_image = this.profile.thumbnail || this.profile.image;
          data.poster_name = this.profile.name || `${this.profile.first_name} ${this.profile.last_name}`;

          this.onPosted?.(data);
          this.comments.unshift(data);
        })
        .catch(error => {
          this.loading = false;
          this.showErrorMessage(error?.response?.data?.message || "Error posting comment");
        });
    },

onUpdatedComment(updated) {
  this.comments = this.comments.map(c =>
    c._id !== updated._id
      ? c
      : { ...c, ...updated } // merge safely to avoid losing poster info
  );
},

    onDeletedComment(data) {
      this.comments = this.comments.filter(c => c._id !== data._id);
      this.$emit("comment-deleted", data);
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },

    loadMoreCommentsAction() {
      this.loading = true;
      this.loadMoreComments({ _id: this._id, skip: this.comments.length })
        .then(data => {
          this.loading = false;
          this.comments = [...new Set([...this.comments, ...data])];
        })
        .catch(error => {
          this.loading = false;
          this.showErrorMessage(error?.response?.data?.message || "Error loading comments");
        });
    },

    goToProfile(poster, type) {
      const routes = { Agency: "agency-profile-details", Volunteer: "volunteer-profile-details" };
      if (routes[type]) this.$router.push({ name: routes[type], params: { id: poster } });
    }
  },
  mounted() {
    if (!this._id) return;
    this.loading = true;
    this.setEventCommentLoaded(false);
    this.getComments({ _id: this._id, selected: this.commentId })
      .then(data => {
        this.loading = false;
        this.comments = data;
        this.setEventCommentLoaded(true);
      })
      .catch(error => {
        this.loading = false;
        this.showErrorMessage(error?.response?.data?.message || "Error fetching comments");
      });
  }
};
</script>

<style scoped>
.comment-section {
  display: flex;
  flex-direction: column;
}
.remove_button {
  position: absolute;
  top: 0;
  right: 0;
}
</style>

<style scoped>
.comment-section {
  border: solid;
  border-radius: 20px;
  border-width: 1px;
  border-color: #c1c1c1;
  width: 100%;
}
.remove_button {
  position: absolute;
  top: 5px;
  right: 14px;
}
</style>
